import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button, InputNumber, Select, showToast, Text } from '@mercai/clever'

import { ModalOrderCustomsCostWarning } from '@/components/modal-order-customs-cost-warning'
import { ModalOrderInfosChangeWarning } from '@/components/modal-order-infos-change-warning'
import { useValidationErrors } from '@/helpers'
import { useCalculateLogisticCustomsCosts } from '@/services'
import { SaleOrderItemPackageEnum } from '@/types'

import { schema } from './card-order-product-values-form.constants'

import { DECIMAL_PLACES_DEFAULT } from '@/constants'

import { CardOrderProductValuesFormProps } from './card-order-product-values-form.type'

import styles from './card-order-product-values-form.module.css'

export const CardOrderProductValuesForm = ({
  onChange,
  values,
  onSubmit,
  onCancel,
  isLoading,
  customsCost,
  onChangeCustomsCost,
  saleOrder,
}: CardOrderProductValuesFormProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('componentCardOrderProductValuesForm')

  const isForeignSupplier = saleOrder?.shipments?.[0]?.supplier?.isForeign

  const { data: dataCustomCost, mutateAsync: customsCostMutateAsync } =
    useCalculateLogisticCustomsCosts()

  const newCustomCost = useMemo(
    () => String(dataCustomCost?.data?.customsCost),
    [dataCustomCost?.data?.customsCost],
  )

  const [customCostToSubmit, setCustomCostToSubmit] = useState<string>('')

  const [isOpenValidationCustomCostModal, setIsOpenValidationCustomCostModal] =
    useState(false)
  const [isOpenValidationModal, setIsOpenValidationModal] = useState(false)
  const [changedTypes, setChangedTypes] = useState<
    ('order_item_price' | 'order_item_cost' | 'order_item_quantity')[]
  >([])

  const onHandleChangedTypes = (
    key: 'order_item_price' | 'order_item_cost' | 'order_item_quantity',
  ) => {
    if (!changedTypes.includes(key)) {
      setChangedTypes((prev) => [...prev, key])
    }
  }

  const { isValid, errors, onHandleListenErrors } = useValidationErrors(
    schema(t),
    values,
  )

  const resetValues = () => {
    setChangedTypes([])
    setCustomCostToSubmit('')
  }

  const onHandleValues = (
    key: string,
    value: boolean | string | number | undefined,
  ) => {
    onChange({
      ...values,
      [key]: value,
    })
  }

  const handleOnSubmit = async () => {
    if (!isValid) {
      onHandleListenErrors(true)
      return
    }

    onHandleListenErrors(false)
    await onSubmit()
    resetValues()
  }

  const validateBeforeSubmit = () => {
    if (changedTypes?.length > 0 && isForeignSupplier) {
      setIsOpenValidationCustomCostModal(true)
      return
    } else if (changedTypes?.length > 0) {
      setIsOpenValidationModal(true)
      return
    }

    handleOnSubmit()
  }

  const handSubmitWithCustomsCost = async (customCost?: string) => {
    if (!isForeignSupplier) {
      return handleOnSubmit()
    }

    if (customCost || customCostToSubmit) {
      const response = await onChangeCustomsCost(
        customCost || customCostToSubmit,
      )

      if (!response?.success) {
        return showToast({
          variant: 'error',
          title: response?.message,
          position: 'bottom-left',
        })
      }
    }

    handleOnSubmit()
  }

  const onCustomsCostChange = (change: boolean) => {
    setCustomCostToSubmit(change ? newCustomCost : '')
    setIsOpenValidationCustomCostModal(false)

    if (changedTypes?.length > 0) {
      return setIsOpenValidationModal(true)
    }

    handSubmitWithCustomsCost(change ? newCustomCost : '')
  }

  useEffect(() => {
    if (isOpenValidationCustomCostModal) {
      customsCostMutateAsync({
        items: saleOrder?.items?.map((product) => ({
          cost:
            values?.uuid === product?.uuid
              ? Number(values?.cost)
              : Number(product?.cost),
          quantity:
            values?.uuid === product?.uuid
              ? values?.quantity || 0
              : product?.quantity,
        })),
        includeIva: saleOrder?.includeIva || false,
      })
    }
  }, [isOpenValidationCustomCostModal])

  return (
    <div className={styles['order-product-prices']}>
      <Text variant="text-medium">
        <strong>{t('orderProductValuesTitle')}</strong>
      </Text>
      <div className={styles['order-product-prices-content']}>
        <div>
          <Text variant="text-base" className={styles.label}>
            {t('orderProductValuesUnitCostMercaiLabel')} <span>*</span>
          </Text>
          <InputNumber
            showIcons
            value={values?.cost}
            onChange={(newValue) => {
              onHandleValues('cost', Number(newValue)?.toFixed?.(DECIMAL_PLACES_DEFAULT))
              onHandleChangedTypes('order_item_cost')
            }}
            placeholder={t('orderProductValuesUnitCostMercaiPlaceholder')}
            decimalPrecision={DECIMAL_PLACES_DEFAULT}
            prefix="$ "
            error={errors?.cost as string}
          />
        </div>
        <div>
          <Text variant="text-base" className={styles.label}>
            {t('orderProductValuesTraderCommissionLabel')}
          </Text>
          <InputNumber
            showIcons
            value={values?.traderCommission}
            onChange={(newValue) =>
              onHandleValues('traderCommission', newValue)
            }
            placeholder={t('orderProductValuesTraderCommissionPlaceholder')}
            decimalPrecision={5}
            prefix="$ "
            error={errors?.traderCommission as string}
          />
        </div>

        <div>
          <Text variant="text-base" className={styles.label}>
            {t('orderProductValuesPriceLabel')} <span>*</span>
          </Text>
          <InputNumber
            showIcons
            value={values?.price}
            onChange={(newValue) => {
              onHandleValues('price', newValue)
              onHandleChangedTypes('order_item_price')
            }}
            placeholder={t('orderProductValuesPricePlaceholder')}
            decimalPrecision={DECIMAL_PLACES_DEFAULT}
            prefix="$ "
            error={errors?.price as string}
          />
        </div>

        <div>
          <Text variant="text-base" className={styles.label}>
            {t('orderProductValuesQuantityLabel')} <span>*</span>
          </Text>
          <InputNumber
            showIcons
            value={values?.quantity}
            onChange={(newValue) => {
              onHandleValues('quantity', newValue)
              onHandleChangedTypes('order_item_quantity')
            }}
            placeholder={t('orderProductValuesQuantityPlaceholder')}
            decimalPrecision={0}
            suffix=" kg"
            error={errors?.quantity as string}
          />
        </div>

        <div>
          <Text variant="text-base" className={styles.label}>
            {t('orderProductValuesPackageLabel')} <span>*</span>
          </Text>
          <Select
            placeholder={t('orderProductValuesPackagePlaceholder')}
            options={Object.entries(SaleOrderItemPackageEnum).map(([key]) => ({
              label: tEnums(`order_itens_package_${key}`),
              value: key,
            }))}
            value={values?.package}
            onChange={(newValue) => {
              onHandleValues('package', newValue)
            }}
            error={errors?.package as string}
          />
        </div>
      </div>

      <div className={styles['order-product-prices-actions']}>
        <Button variant="secondary" onClick={onCancel}>
          {t('orderProductValuesCancelButton')}
        </Button>
        <Button
          type="button"
          onClick={validateBeforeSubmit}
          isLoading={isLoading}
        >
          {t('orderProductValuesSaveButton')}
        </Button>
      </div>

      <ModalOrderCustomsCostWarning
        isOpen={isOpenValidationCustomCostModal}
        onClose={() => {
          setIsOpenValidationCustomCostModal(false)
          setCustomCostToSubmit('')
        }}
        actualValue={customsCost}
        newValue={newCustomCost}
        onSave={onCustomsCostChange}
      />

      <ModalOrderInfosChangeWarning
        isOpen={isOpenValidationModal}
        onClose={() => {
          setIsOpenValidationModal(false)
        }}
        saleOrderUuid={saleOrder?.uuid}
        onSave={handSubmitWithCustomsCost}
        types={changedTypes}
      />
    </div>
  )
}
